import React, { useEffect, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * OperatorMiniMap — tiny live map on the FEL operator dashboard.
 *  • Self marker (blue) with 100 m visibility ring
 *  • Nearby operator markers, colour-coded by proximity zone:
 *        visible  ≤ 100 m  → slate
 *        warn     ≤  30 m  → amber, dashed 30 m ring on self
 *        alarm    ≤  10 m  → red   pulsing marker
 *  • Auto-recenters on the operator; falls back to a placeholder when GPS is not fixed yet.
 */
export default function OperatorMiniMap({ selfPos, nearby, machineNumber, zones, basemap = "satellite", height = 140 }) {
    const center = selfPos && selfPos.lat != null ? [selfPos.lat, selfPos.lng] : [-28.7826, 32.0377];
    const visible = zones?.visible_m ?? 100;
    const warn    = zones?.warn_m    ?? 30;
    const alarm   = zones?.alarm_m   ?? 10;

    // Compute worst zone from `nearby` — dictates the pulsing halo colour on self.
    const worst = useMemo(() => {
        if (!nearby || !nearby.length) return null;
        if (nearby.some((n) => n.zone === "alarm")) return "alarm";
        if (nearby.some((n) => n.zone === "warn"))  return "warn";
        return "visible";
    }, [nearby]);

    const tileUrl = basemap === "satellite"
        ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        : "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
    const tileAttr = basemap === "satellite"
        ? '&copy; Esri, Maxar, Earthstar Geographics'
        : '&copy; CARTO · OpenStreetMap';

    return (
        <div className="rounded-md overflow-hidden ring-1 ring-slate-800 shadow-inner relative" style={{ height }} data-testid="operator-mini-map">
            <MapContainer center={center} zoom={19} style={{ height: "100%", width: "100%", background: "#334155" }} zoomControl={false} attributionControl={false} scrollWheelZoom={false}>
                <TileLayer url={tileUrl} attribution={tileAttr} subdomains="abcd" maxZoom={20} />
                <Recenter center={center} />
                {selfPos && selfPos.lat != null && (
                    <>
                        {/* 100 m visibility ring */}
                        <Circle center={center} radius={visible}
                                pathOptions={{ color: "#38BDF8", weight: 1, opacity: 0.55, fillColor: "#38BDF8", fillOpacity: 0.04 }} />
                        {/* 30 m warn ring — only visible when someone is within warn/alarm */}
                        {worst && worst !== "visible" && (
                            <Circle center={center} radius={warn}
                                    pathOptions={{ color: "#F59E0B", weight: 1.5, opacity: 0.85, dashArray: "4 4", fillOpacity: 0 }} />
                        )}
                        {/* 10 m alarm ring — only when at least one operator is inside it */}
                        {worst === "alarm" && (
                            <Circle center={center} radius={alarm}
                                    pathOptions={{ color: "#DC2626", weight: 2, opacity: 1, fillColor: "#DC2626", fillOpacity: 0.08 }} />
                        )}
                        <Marker position={center} icon={selfIcon(machineNumber, worst)} />
                    </>
                )}
                {(nearby || []).map((n) => (
                    n.lat != null && n.lng != null && (
                        <Marker key={n.session_id} position={[n.lat, n.lng]} icon={otherIcon(n)} />
                    )
                ))}
            </MapContainer>
            {(!selfPos || selfPos.lat == null) && (
                <div className="absolute inset-0 grid place-items-center pointer-events-none bg-slate-900/60 text-slate-100 text-[11px] uppercase tracking-widest font-bold">
                    Waiting for GPS fix…
                </div>
            )}
        </div>
    );
}

function Recenter({ center }) {
    const map = useMap();
    useEffect(() => {
        if (center && center[0] != null) map.setView(center, map.getZoom(), { animate: true });
    }, [center, map]);
    return null;
}

function selfIcon(label = "FEL", worst = null) {
    const ring = worst === "alarm" ? "#DC2626" : worst === "warn" ? "#F59E0B" : "#38BDF8";
    const pulse = worst ? `animation:op-pulse 1.4s ease-out infinite;` : "";
    return L.divIcon({
        className: "",
        html: `
            <div style="position:relative;width:44px;height:44px;">
                <style>@keyframes op-pulse { 0%{box-shadow:0 0 0 0 ${ring}CC;} 100%{box-shadow:0 0 0 14px ${ring}00;} }</style>
                <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:22px;height:22px;border-radius:50%;background:#0F1E37;color:#fff;border:3px solid ${ring};display:flex;align-items:center;justify-content:center;font:900 9px/1 Inter,system-ui;letter-spacing:.05em;${pulse}">${(label || "ME").toString().slice(0, 4).toUpperCase()}</div>
            </div>`,
        iconSize: [44, 44], iconAnchor: [22, 22],
    });
}

function otherIcon(n) {
    const color = n.zone === "alarm" ? "#DC2626" : n.zone === "warn" ? "#F59E0B" : "#64748B";
    const label = (n.machine || "FEL").toString().slice(0, 4).toUpperCase();
    return L.divIcon({
        className: "",
        html: `
            <div style="position:relative;">
                <div style="width:22px;height:22px;border-radius:50%;background:${color};color:#fff;border:2px solid #fff;display:flex;align-items:center;justify-content:center;font:900 9px/1 Inter,system-ui;letter-spacing:.05em;box-shadow:0 1px 3px rgba(0,0,0,.6);">${label}</div>
                <div style="position:absolute;top:22px;left:50%;transform:translateX(-50%);background:${color};color:#fff;padding:1px 4px;border-radius:2px;font:800 9px/1 Inter,system-ui;white-space:nowrap;margin-top:2px;">${n.distance_m.toFixed(0)} m</div>
            </div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
    });
}
